import { Button, buttonVariants } from "@loot-os/ui/components/button";
import { cn } from "@loot-os/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import { type LucideIcon, MenuIcon, MessagesSquareIcon } from "lucide-react";

import type { NavLink } from "../lib/nav.js";

/** One tab: the icon over its label, the whole cell the tap target. */
const TAB = "h-auto min-w-12 flex-col gap-0 px-0 py-0 text-xs font-bold hover:bg-transparent";

/**
 * The design's phone tab bar, below `md`: the menu, each game the store has on, and chat when the
 * store has it. Fixed to the bottom, so `App` pads the page by its height.
 */
export function BottomNav({
  games,
  onOpenMenu,
  onOpenChat,
}: {
  games: NavLink[];
  onOpenMenu: () => void;
  /** Absent while the store has chat off. */
  onOpenChat?: () => void;
}) {
  return (
    <nav
      aria-label="Tab bar"
      className="fixed inset-x-0 bottom-0 z-40 bg-background px-6 pt-1.5 pb-5 md:hidden"
    >
      <div className="flex items-center justify-around">
        <Button variant="ghost" className={cn(TAB, "text-muted-foreground")} onClick={onOpenMenu}>
          <TabIcon icon={MenuIcon} />
          Menu
        </Button>
        {games.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            activeOptions={{ exact: link.exact }}
            className={cn(buttonVariants({ variant: "ghost" }), TAB)}
            activeProps={{ className: "text-foreground" }}
            inactiveProps={{ className: "text-muted-foreground" }}
          >
            <TabIcon icon={link.icon} />
            <span className="max-w-20 truncate">{link.label}</span>
          </Link>
        ))}
        {onOpenChat && (
          <Button variant="ghost" className={cn(TAB, "text-muted-foreground")} onClick={onOpenChat}>
            <TabIcon icon={MessagesSquareIcon} />
            Chat
          </Button>
        )}
      </div>
    </nav>
  );
}

function TabIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="flex size-6 items-center justify-center text-foreground">
      <Icon className="size-4" />
    </span>
  );
}
